import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { apiError, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  ArrowLeft, Mic, Sparkles, Loader2, Play, Pause, FileDown, Film,
  CheckCircle2, Search, Wand2, Scissors, Volume2, Smartphone, Download,
  Flame, FileCode, Captions, Music, Users,
} from "lucide-react";
import { toast } from "sonner";

const fmt = (s) => {
  s = Math.max(0, s || 0);
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
};

const toneColor = {
  drama: "#FF2E93", suspense: "#8B5CF6", comédia: "#F59E0B",
  comedia: "#F59E0B", romance: "#00E5FF", treta: "#EF4444",
};

export default function Editor() {
  const { vid } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isProducer = user?.role === "producer";
  const videoRef = useRef(null);
  const [video, setVideo] = useState(null);
  const [transcription, setTranscription] = useState(null);
  const [script, setScript] = useState(null);
  const [current, setCurrent] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [search, setSearch] = useState("");
  const [exports, setExports] = useState([]);
  const [busy, setBusy] = useState(null); // roughcut | vertical | narration | tension
  const [tension, setTension] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [cc, setCc] = useState(true);
  const [trilha, setTrilha] = useState(false);

  const load = async () => {
    const v = await api.get(`/videos/${vid}`);
    setVideo(v.data);
    api.get(`/videos/${vid}/transcription`).then((r) => setTranscription(r.data)).catch(() => setTranscription(false));
    api.get(`/videos/${vid}/script`).then((r) => setScript(r.data)).catch(() => setScript(false));
    api.get(`/videos/${vid}/exports`).then((r) => setExports(r.data)).catch(() => setExports([]));
    api.get(`/videos/${vid}/tension`).then((r) => setTension(r.data)).catch(() => setTension(false));
    api.get(`/projects/${v.data.project_id}/participants`).then((r) => setParticipants(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, [vid]);

  const loadExports = () => api.get(`/videos/${vid}/exports`).then((r) => setExports(r.data)).catch(() => {});

  const src = video?.storage_path ? `${API}/videos/${vid}/stream` : video?.external_url;
  const canTranscribe = !!video?.storage_path;

  const seek = (t) => {
    if (videoRef.current) {
      videoRef.current.currentTime = t;
      videoRef.current.play();
    }
  };

  const togglePlay = () => {
    if (!videoRef.current) return;
    if (videoRef.current.paused) videoRef.current.play();
    else videoRef.current.pause();
  };

  const transcribe = async () => {
    setTranscribing(true);
    try {
      const { data } = await api.post(`/videos/${vid}/transcribe`);
      setTranscription(data);
      toast.success("Transcrição concluída");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally { setTranscribing(false); }
  };

  const generate = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post(`/videos/${vid}/script/generate`);
      setScript(data);
      toast.success("Roteiro gerado pela IA");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally { setGenerating(false); }
  };

  const approve = async () => {
    try {
      await api.post(`/scripts/${script.id}/approve`);
      setScript({ ...script, status: "approved", approved_by: user.name });
      toast.success("Roteiro aprovado");
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const roughcut = async () => {
    setBusy("roughcut");
    try {
      const { data } = await api.post(`/videos/${vid}/roughcut`, { subtitles: cc, soundtrack: trilha });
      const extras = [data.meta?.subtitles && "legendas", data.meta?.soundtrack && "trilha de tensão"].filter(Boolean).join(" + ");
      toast.success(`Rough cut renderizado (${data.meta.clips} cortes, ${data.meta.duration}s)${extras ? ` com ${extras}` : " — qualidade original preservada"}`);
      await loadExports();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  const verticals = async () => {
    setBusy("vertical");
    try {
      const { data } = await api.post(`/videos/${vid}/verticals`, { subtitles: cc });
      toast.success(`${data.count} cortes verticais 9:16 gerados${cc ? " com legendas" : ""} para Reels/TikTok/Shorts`);
      await loadExports();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  const detectTension = async () => {
    setBusy("tension");
    try {
      const { data } = await api.post(`/videos/${vid}/tension`);
      setTension(data);
      toast.success(`Análise concluída: ${data.peaks.length} pico(s) de treta detectado(s)`);
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  const identifySpeakers = async () => {
    setBusy("speakers");
    try {
      const { data } = await api.post(`/videos/${vid}/speakers`);
      setTranscription(data);
      toast.success("Falas vinculadas aos participantes do elenco");
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  const speakerColor = (name) => participants.find((p) => p.name === name)?.color || null;

  const narration = async () => {
    setBusy("narration");
    try {
      await api.post(`/videos/${vid}/narration`);
      toast.success("Narração em voz gerada");
      await loadExports();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  const updateMoment = (i, key, value) => {
    const moments = script.moments.map((m, idx) => (idx === i ? { ...m, [key]: value } : m));
    setScript({ ...script, moments });
  };

  const saveScript = async () => {
    try {
      await api.put(`/scripts/${script.id}`, { moments: script.moments, teaser: script.teaser });
      toast.success("Roteiro salvo");
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  if (!video) return <div className="h-screen flex items-center justify-center bg-[#08080C]"><Loader2 className="w-6 h-6 animate-spin text-[#FF2E93]" /></div>;

  const segs = transcription?.segments || [];
  const filtered = search ? segs.filter((s) => s.text.toLowerCase().includes(search.toLowerCase())) : segs;
  const activeIdx = segs.findIndex((s) => current >= s.start && current < s.end);

  return (
    <div className="h-screen flex flex-col bg-[#08080C] text-[#F4F4F5]">
      <div className="grain" />
      {/* Top bar */}
      <header className="h-14 border-b border-[#282834] flex items-center px-4 gap-4 shrink-0 z-10">
        <button onClick={() => navigate(-1)} data-testid="editor-back" className="text-[#A1A1AA] hover:text-[#F4F4F5] flex items-center gap-2 text-sm transition-colors duration-200">
          <ArrowLeft className="w-4 h-4" /> Voltar
        </button>
        <span className="text-sm font-mono truncate text-[#A1A1AA]">{video.title}</span>
        <div className="ml-auto flex items-center gap-2">
          <button onClick={() => setCc(!cc)} data-testid="toggle-subtitles" title="Legendas estilizadas queimadas nos exports"
            className={`text-xs rounded-full px-3 py-2 flex items-center gap-1.5 border transition-colors duration-200 ${cc ? "bg-[#00E5FF]/15 border-[#00E5FF] text-[#00E5FF]" : "bg-[#1C1C24] border-[#282834] text-[#555]"}`}>
            <Captions className="w-3.5 h-3.5" /> Legendas
          </button>
          <button onClick={() => setTrilha(!trilha)} data-testid="toggle-soundtrack" title="Stingers de tensão automáticos no rough cut"
            className={`text-xs rounded-full px-3 py-2 flex items-center gap-1.5 border transition-colors duration-200 ${trilha ? "bg-[#8B5CF6]/15 border-[#8B5CF6] text-[#8B5CF6]" : "bg-[#1C1C24] border-[#282834] text-[#555]"}`}>
            <Music className="w-3.5 h-3.5" /> Trilha
          </button>
          <span className="w-px h-5 bg-[#282834]" />
          <a href={`${API}/videos/${vid}/edl`} data-testid="export-edl-button"
            className={`text-sm rounded-full px-4 py-2 flex items-center gap-2 transition-colors duration-200 ${script ? "bg-[#1C1C24] border border-[#282834] hover:border-[#00E5FF] text-[#F4F4F5]" : "bg-[#141419] text-[#555] pointer-events-none"}`}>
            <FileDown className="w-4 h-4" /> EDL
          </a>
          <a href={`${API}/videos/${vid}/xml`} data-testid="export-xml-button" title="Timeline XML para Premiere/DaVinci"
            className={`text-sm rounded-full px-4 py-2 flex items-center gap-2 transition-colors duration-200 ${script ? "bg-[#1C1C24] border border-[#282834] hover:border-[#00E5FF] text-[#F4F4F5]" : "bg-[#141419] text-[#555] pointer-events-none"}`}>
            <FileCode className="w-4 h-4" /> XML
          </a>
          <button onClick={narration} disabled={!script || busy} data-testid="narration-button"
            className="text-sm rounded-full px-4 py-2 flex items-center gap-2 bg-[#1C1C24] border border-[#282834] hover:border-[#00E5FF] text-[#F4F4F5] transition-colors duration-200 disabled:opacity-40">
            {busy === "narration" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Volume2 className="w-4 h-4 text-[#00E5FF]" />} Narração
          </button>
          <button onClick={verticals} disabled={!script || busy} data-testid="verticals-button"
            className="text-sm rounded-full px-4 py-2 flex items-center gap-2 bg-[#1C1C24] border border-[#282834] hover:border-[#FF2E93] text-[#F4F4F5] transition-colors duration-200 disabled:opacity-40">
            {busy === "vertical" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Smartphone className="w-4 h-4 text-[#FF2E93]" />} 9:16
          </button>
          <button onClick={roughcut} disabled={!script || busy} data-testid="roughcut-button"
            className="text-sm rounded-full px-4 py-2 flex items-center gap-2 bg-[#FF2E93] hover:bg-[#ff54a6] text-white transition-colors duration-200 disabled:opacity-40">
            {busy === "roughcut" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Scissors className="w-4 h-4" />} Rough Cut
          </button>
        </div>
      </header>

      {/* 3-column workspace */}
      <div className="flex-1 grid grid-cols-1 lg:grid-cols-[1.6fr_1fr_1fr] min-h-0">
        {/* Player + timeline */}
        <div className="border-r border-[#282834] flex flex-col min-h-0">
          <div className="bg-black flex items-center justify-center relative" style={{ aspectRatio: "16/9" }}>
            <video
              preload="metadata"
              ref={videoRef} src={src} className="w-full h-full object-contain"
              data-testid="video-player"
              onTimeUpdate={(e) => setCurrent(e.target.currentTime)}
              onPlay={() => setPlaying(true)} onPause={() => setPlaying(false)}
              crossOrigin="anonymous"
            />
          </div>
          <div className="p-4 flex items-center gap-4 border-b border-[#282834]">
            <button onClick={togglePlay} data-testid="play-toggle" className="w-11 h-11 rounded-full bg-[#FF2E93] hover:bg-[#ff54a6] flex items-center justify-center transition-colors duration-200">
              {playing ? <Pause className="w-5 h-5 text-white" /> : <Play className="w-5 h-5 text-white ml-0.5" />}
            </button>
            <span className="font-mono text-sm text-[#A1A1AA]">{fmt(current)}</span>
          </div>

          {/* Timeline with cut points */}
          <div className="flex-1 p-4 overflow-y-auto min-h-0">
            <div className="flex items-center gap-2 mb-3">
              <Film className="w-4 h-4 text-[#00E5FF]" />
              <h3 className="font-head font-600 text-sm">Linha do tempo — cortes sugeridos</h3>
            </div>
            {script && script.moments?.length ? (
              <div className="space-y-2">
                {script.moments.map((m, i) => (
                  <button key={i} onClick={() => seek(m.start)} data-testid={`timeline-cut-${i}`}
                    className="w-full text-left bg-[#121218] border border-[#282834] rounded-lg p-3 hover:border-[#FF2E93] transition-colors duration-200"
                    style={{ borderLeft: `3px solid ${toneColor[m.tone] || "#FF2E93"}` }}>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">{m.title}</span>
                      <span className="text-xs font-mono text-[#A1A1AA]">{fmt(m.start)} → {fmt(m.end)}</span>
                    </div>
                    <span className="text-[10px] uppercase tracking-wider" style={{ color: toneColor[m.tone] || "#FF2E93" }}>{m.tone}</span>
                  </button>
                ))}
              </div>
            ) : (
              <p className="text-sm text-[#555]">Gere o roteiro para ver os cortes na timeline.</p>
            )}

            {/* Entregas / exports */}
            {exports.length > 0 && (
              <div className="mt-6" data-testid="deliveries-panel">
                <div className="flex items-center gap-2 mb-3">
                  <Download className="w-4 h-4 text-[#FF2E93]" />
                  <h3 className="font-head font-600 text-sm">Entregas</h3>
                </div>
                <div className="space-y-2">
                  {exports.map((e) => (
                    <div key={e.id} data-testid={`export-${e.type}-${e.id}`} className="bg-[#121218] border border-[#282834] rounded-lg p-3">
                      {e.type === "narration" ? (
                        <div>
                          <p className="text-xs text-[#00E5FF] mb-2 flex items-center gap-1"><Volume2 className="w-3 h-3" /> {e.filename}</p>
                          <audio controls src={`${API}/exports/${e.id}/download`} className="w-full h-9" data-testid={`audio-${e.id}`} />
                        </div>
                      ) : (
                        <div className="flex items-center justify-between gap-2">
                          <div className="min-w-0">
                            <p className="text-sm truncate flex items-center gap-1.5">
                              {e.type === "vertical" ? <Smartphone className="w-3.5 h-3.5 text-[#FF2E93]" /> : <Scissors className="w-3.5 h-3.5 text-[#FF2E93]" />}
                              {e.filename}
                            </p>
                            <p className="text-[10px] text-[#A1A1AA]">{(e.size / 1024 / 1024).toFixed(1)} MB{e.meta?.duration ? ` · ${e.meta.duration}s` : ""}</p>
                          </div>
                          <a href={`${API}/exports/${e.id}/download?download=1`} data-testid={`download-${e.id}`}
                            className="shrink-0 text-xs bg-[#1C1C24] border border-[#282834] hover:border-[#00E5FF] rounded-full px-3 py-1.5 flex items-center gap-1 transition-colors duration-200">
                            <Download className="w-3 h-3" /> Baixar
                          </a>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Transcription */}
        <div className="border-r border-[#282834] flex flex-col min-h-0">
          <div className="p-4 border-b border-[#282834]">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-head font-600 text-sm flex items-center gap-2"><Mic className="w-4 h-4 text-[#00E5FF]" /> Transcrição</h3>
              <div className="flex items-center gap-2">
                {transcription && (
                  <button onClick={identifySpeakers} disabled={busy === "speakers"} data-testid="identify-speakers-button"
                    className="text-xs bg-[#1C1C24] border border-[#282834] hover:border-[#F59E0B] rounded-full px-3 py-1.5 flex items-center gap-1.5 transition-colors duration-200 disabled:opacity-60">
                    {busy === "speakers" ? <Loader2 className="w-3 h-3 animate-spin" /> : <Users className="w-3 h-3 text-[#F59E0B]" />}
                    Falantes
                  </button>
                )}
                {transcription && (
                  <button onClick={detectTension} disabled={busy === "tension"} data-testid="detect-tension-button"
                    className="text-xs bg-[#1C1C24] border border-[#282834] hover:border-[#EF4444] rounded-full px-3 py-1.5 flex items-center gap-1.5 transition-colors duration-200 disabled:opacity-60">
                    {busy === "tension" ? <Loader2 className="w-3 h-3 animate-spin" /> : <Flame className="w-3 h-3 text-[#EF4444]" />}
                    {tension ? "Reanalisar treta" : "Detectar treta"}
                  </button>
                )}
                {canTranscribe && (
                  <button onClick={transcribe} disabled={transcribing} data-testid="transcribe-button"
                    className="text-xs bg-[#1C1C24] border border-[#282834] hover:border-[#00E5FF] rounded-full px-3 py-1.5 flex items-center gap-1.5 transition-colors duration-200 disabled:opacity-60">
                    {transcribing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3 text-[#00E5FF]" />}
                    {transcription ? "Refazer" : "Transcrever"}
                  </button>
                )}
              </div>
            </div>
            <div className="relative">
              <Search className="w-4 h-4 text-[#555] absolute left-3 top-1/2 -translate-y-1/2" />
              <input value={search} onChange={(e) => setSearch(e.target.value)} data-testid="transcript-search"
                placeholder="Buscar momento/palavra..."
                className="w-full bg-[#08080C] border border-[#282834] rounded-lg pl-9 pr-3 py-2 text-sm focus:outline-none focus:border-[#00E5FF] transition-colors duration-200" />
            </div>
            {tension && tension.points?.length > 0 && (
              <div className="mt-3">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[10px] uppercase tracking-wider text-[#EF4444] flex items-center gap-1"><Flame className="w-3 h-3" /> Mapa de tensão</span>
                  <span className="text-[10px] text-[#555]">{tension.peaks.length} pico(s)</span>
                </div>
                <div className="flex h-2.5 rounded-full overflow-hidden gap-px" data-testid="tension-heatmap">
                  {tension.points.map((p, i) => (
                    <button key={i} onClick={() => seek(p.start)} title={`${p.score} · ${p.label} · ${p.text.slice(0, 60)}`}
                      className="flex-1 min-w-0 transition-opacity duration-200 hover:opacity-70"
                      style={{ background: `rgba(239,68,68,${0.12 + (p.score / 100) * 0.88})` }} />
                  ))}
                </div>
              </div>
            )}
          </div>
          <div className="flex-1 overflow-y-auto p-3 space-y-1 min-h-0" data-testid="transcript-panel">
            {transcription === null && <Loader2 className="w-4 h-4 animate-spin text-[#555] mt-4 mx-auto" />}
            {transcription === false && (
              <div className="text-center mt-8 px-4">
                <p className="text-sm text-[#555]">
                  {video.source === "upload"
                    ? "Sem transcrição ainda. Clique em Transcrever para rodar o Whisper em PT-BR."
                    : canTranscribe
                    ? "Sem transcrição ainda. Clique em Transcrever para rodar o Whisper em PT-BR."
                    : "Vídeos do Drive demo não são transcritos. Envie um arquivo (upload) ou conecte o Google Drive real."}
                </p>
              </div>
            )}
            {filtered.map((s) => {
              const idx = segs.indexOf(s);
              const active = idx === activeIdx;
              const tp = tension && tension.points ? tension.points[idx] : null;
              return (
                <button key={idx} onClick={() => seek(s.start)} data-testid={`transcript-seg-${idx}`}
                  className={`w-full text-left rounded-lg px-3 py-2 transition-colors duration-200 ${active ? "bg-[#00E5FF]/10 border-l-2 border-[#00E5FF]" : "hover:bg-[#121218] border-l-2 border-transparent"}`}>
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="text-[10px] font-mono text-[#00E5FF]">{fmt(s.start)}</span>
                    {s.speaker && (
                      <span className="text-[10px] font-semibold" style={{ color: speakerColor(s.speaker) || "#A1A1AA" }}>
                        {s.speaker}
                      </span>
                    )}
                    {tp && tp.score >= 70 && (
                      <span className="text-[10px] ml-auto flex items-center gap-1 text-[#EF4444] bg-[#EF4444]/10 rounded-full px-2 py-0.5" data-testid={`tension-peak-${idx}`}>
                        <Flame className="w-3 h-3" /> {tp.score} {tp.label}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-[#F4F4F5] leading-snug">{s.text}</p>
                </button>
              );
            })}
          </div>
        </div>

        {/* Script */}
        <div className="flex flex-col min-h-0">
          <div className="p-4 border-b border-[#282834] flex items-center justify-between">
            <h3 className="font-head font-600 text-sm flex items-center gap-2"><Wand2 className="w-4 h-4 text-[#FF2E93]" /> Roteiro IA</h3>
            <div className="flex items-center gap-2">
              {transcription && (
                <button onClick={generate} disabled={generating} data-testid="generate-script-button"
                  className="text-xs bg-[#FF2E93] hover:bg-[#ff54a6] text-white rounded-full px-3 py-1.5 flex items-center gap-1.5 transition-colors duration-200 disabled:opacity-60">
                  {generating ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                  {script ? "Regerar" : "Gerar"}
                </button>
              )}
            </div>
          </div>
          <div className="flex-1 overflow-y-auto p-4 min-h-0" data-testid="script-panel">
            {script === null && <Loader2 className="w-4 h-4 animate-spin text-[#555] mt-4 mx-auto" />}
            {script === false && (
              <p className="text-sm text-[#555] mt-6 text-center">
                {transcription ? "Clique em Gerar para a IA montar o roteiro no estilo do programa." : "Transcreva o vídeo primeiro para gerar o roteiro."}
              </p>
            )}
            {script && (
              <div className="space-y-4">
                {script.status === "approved" && (
                  <div className="flex items-center gap-2 text-xs text-[#10B981] bg-[#10B981]/10 rounded-lg px-3 py-2">
                    <CheckCircle2 className="w-4 h-4" /> Aprovado por {script.approved_by}
                  </div>
                )}
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-[#00E5FF]">Chamada de abertura</label>
                  <textarea value={script.teaser} onChange={(e) => setScript({ ...script, teaser: e.target.value })}
                    data-testid="script-teaser"
                    className="mt-1 w-full bg-[#121218] border border-[#282834] rounded-lg p-3 text-sm focus:outline-none focus:border-[#FF2E93] transition-colors duration-200 resize-none h-20" />
                </div>
                {script.resumo_editorial && (
                  <p className="text-sm text-[#A1A1AA] italic border-l-2 border-[#282834] pl-3">{script.resumo_editorial}</p>
                )}

                <div className="space-y-3">
                  {script.moments.map((m, i) => (
                    <div key={i} data-testid={`script-moment-${i}`} className="bg-[#121218] border border-[#282834] rounded-lg p-3" style={{ borderLeft: `3px solid ${toneColor[m.tone] || "#FF2E93"}` }}>
                      <div className="flex items-center justify-between mb-2">
                        <input value={m.title} onChange={(e) => updateMoment(i, "title", e.target.value)}
                          className="bg-transparent text-sm font-medium flex-1 focus:outline-none" />
                        <button onClick={() => seek(m.start)} className="text-[10px] font-mono text-[#00E5FF] hover:text-[#FF2E93] transition-colors duration-200">{fmt(m.start)}→{fmt(m.end)}</button>
                      </div>
                      <span className="text-[10px] uppercase tracking-wider" style={{ color: toneColor[m.tone] || "#FF2E93" }}>{m.tone}</span>
                      <textarea value={m.narration} onChange={(e) => updateMoment(i, "narration", e.target.value)}
                        data-testid={`moment-narration-${i}`}
                        className="mt-2 w-full bg-[#08080C] border border-[#282834] rounded p-2 text-xs text-[#F4F4F5] focus:outline-none focus:border-[#FF2E93] transition-colors duration-200 resize-none h-16" />
                    </div>
                  ))}
                </div>

                <div className="flex gap-2 pt-2">
                  <button onClick={saveScript} data-testid="save-script-button" className="flex-1 bg-[#1C1C24] border border-[#282834] hover:border-[#00E5FF] rounded-lg py-2 text-sm transition-colors duration-200">Salvar</button>
                  {isProducer && script.status !== "approved" && (
                    <button onClick={approve} data-testid="approve-script-button" className="flex-1 bg-[#10B981] hover:bg-[#0ea371] text-white rounded-lg py-2 text-sm transition-colors duration-200 flex items-center justify-center gap-1.5">
                      <CheckCircle2 className="w-4 h-4" /> Aprovar
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
